import React, { useCallback, useEffect, useState } from "react";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import CountrySelector from "../../components/CustomComponents/DropdownSelections/CountrySelector.jsx";
import CurrencySelector from "../../components/CustomComponents/DropdownSelections/CurrencySelector.jsx";
import swal from "sweetalert";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import {
  AGREEMENT_MONTH_PRESETS,
  buildPricePayload,
  formatAgreementMonths,
  isSamePriceTier,
  durationDaysToMonths,
} from "../../utils/packagePricingUtils.js";

function AddPackage() {
  const navigate = useNavigate();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);

  const [packageData, setPackageData] = useState({
    pName: "",
    pDetail: "",
    pTrialDays: 0,
    pAllowedDevices: 1,
    pAllowedUsers: 1,
    pStatus: true,
  });

  // Pricing state
  const [prices, setPrices] = useState([]);
  const [newPrice, setNewPrice] = useState({
    currency: null,
    price: 0,
    actualPrice: 0,
    agreementMonths: 3,
    useCustomMonths: false,
    customMonths: "",
    country: "",
  });

  const getEffectiveMonths = (priceRow) => {
    if (priceRow.useCustomMonths) {
      const custom = parseInt(priceRow.customMonths, 10);
      return custom > 0 ? custom : 0;
    }
    return parseInt(priceRow.agreementMonths, 10) || 0;
  };

  const resetNewPriceForm = () => ({
    currency: null,
    price: 0,
    actualPrice: 0,
    agreementMonths: 3,
    useCustomMonths: false,
    customMonths: "",
    country: "",
  });

  const handleInputChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setPackageData({ ...packageData, [field]: value });
  };

  const handlePriceInputChange = (e, field) => {
    setNewPrice({ ...newPrice, [field]: e.target.value });
  };

  const handleAddPrice = () => {
    if (!newPrice.currency) {
      showErrorToast("Please select a currency");
      return;
    }

    if (newPrice.price <= 0) {
      showErrorToast("Price must be greater than 0");
      return;
    }

    const agreementMonths = getEffectiveMonths(newPrice);
    if (!agreementMonths || agreementMonths < 1) {
      showErrorToast("Please enter a valid agreement period in months");
      return;
    }

    // Check if same currency, country, and agreement months already exists
    const exists = prices.find((p) =>
      isSamePriceTier(
        { currency: p.currency, country: p.country, agreementMonths: p.agreementMonths },
        { currency: newPrice.currency, country: newPrice.country, agreementMonths }
      )
    );

    if (exists) {
      showErrorToast(
        `Price for ${newPrice.currency.label} ${newPrice.country ? `(${newPrice.country})` : ""
        } already exists`
      );
      return;
    }

    setPrices([
      ...prices,
      {
        ...newPrice,
        agreementMonths,
        id: Date.now(),
      },
    ]);

    setNewPrice(resetNewPriceForm());

    showSuccessToast("Price added to list");
  };

  const handleRemovePrice = (priceId) => {
    setPrices(prices.filter((p) => p.id !== priceId));
    showSuccessToast("Price removed from list");
  };

  const handleSubmit = async () => {
    const { pName } = packageData;

    if (!pName) {
      showErrorToast("Please fill in package name.");
      return;
    }

    if (prices.length === 0) {
      const result = await swal({
        title: "No Pricing Added",
        text: "You haven't added any pricing for this package. Do you want to continue?",
        icon: "warning",
        buttons: true,
      });

      if (!result) {
        return;
      }
    }

    try {
      setLoading(true);

      // Step 1: Create Package
      const packagePayload = {
        PName: packageData.pName,
        PDetail: packageData.pDetail,
        PTrialDays: parseInt(packageData.pTrialDays) || 0,
        PAllowedDevices: parseInt(packageData.pAllowedDevices) || 1,
        PAllowedUsers: parseInt(packageData.pAllowedUsers) || 1,
        PStatus: packageData.pStatus,
      };

      const packageResponse = await axiosInstance.post(
        APIs.POST_ADD_PACKAGE,
        packagePayload
      );

      if (packageResponse.data.status) {
        const newPackageID = packageResponse.data.data.id;

        // Step 2: Add Prices if any
        if (prices.length > 0) {
          let successCount = 0;
          let failCount = 0;

          for (const price of prices) {
            try {
              const pricePayload = buildPricePayload(newPackageID, price);

              const priceResponse = await axiosInstance.post(
                APIs.POST_ADD_PACKAGE_PRICE,
                pricePayload
              );

              if (priceResponse.data.status) {
                successCount++;
              } else {
                failCount++;
              }
            } catch (err) {
              failCount++;
            }
          }

          if (successCount > 0) {
            showSuccessToast(
              `Package created successfully with ${successCount} price(s)${failCount > 0 ? `, ${failCount} price(s) failed` : ""
              }`
            );
          } else {
            showSuccessToast("Package created but no prices were added");
          }
        } else {
          showSuccessToast("Package created successfully");
        }

        navigate("/packages/managePackages");
      } else {
        showErrorToast(packageResponse.data.message || "Failed to create package");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to create package");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/packages/managePackages">Manage Packages</Link>
            </li>
            <li className="breadcrumb-item active">Add Package</li>
          </ol>
          <h1 className="page-header mb-0">Add New Package</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn btn-success btn-rounded px-4 rounded-pill"
            disabled={loading}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
            Create Package
          </button>
        </div>
      </div>

      {/* Package Details */}
      <Panel>
        <PanelHeader>Package Details</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-12">
              <FormInput
                label="Package Name"
                placeHolder="Enter Package Name"
                type="text"
                value={packageData.pName}
                onchange={(e) => handleInputChange(e, "pName")}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <label>Description</label>
              <textarea
                className="form-control"
                placeholder="Enter package description"
                value={packageData.pDetail}
                onChange={(e) => handleInputChange(e, "pDetail")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Trial Days"
                placeHolder="e.g., 14"
                type="number"
                value={packageData.pTrialDays}
                onchange={(e) => handleInputChange(e, "pTrialDays")}
                min="0"
              />
              <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                Number of free trial days (0 for no trial)
              </small>
            </div>
            <div className="col-6">
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Allowed Devices"
                placeHolder="e.g., 5"
                type="number"
                value={packageData.pAllowedDevices}
                onchange={(e) => handleInputChange(e, "pAllowedDevices")}
                min="0"
              />
            </div>
            <div className="col-6">
              <FormInput
                label="Allowed Users"
                placeHolder="e.g., 10"
                type="number"
                value={packageData.pAllowedUsers}
                onchange={(e) => handleInputChange(e, "pAllowedUsers")}
                min="0"
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="pStatus"
                  checked={packageData.pStatus}
                  onChange={(e) => handleInputChange(e, "pStatus")}
                />
                <label className="form-check-label" htmlFor="pStatus">
                  Active
                </label>
              </div>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Pricing Section */}
      <Panel>
        <PanelHeader>Pricing</PanelHeader>
        <PanelBody>
          <div className="alert alert-info">
            <i className="fa fa-info-circle me-2"></i>
            Add pricing for different currencies and countries. This allows regional pricing for your package.
          </div>

          {/* Add New Price Form */}
          <div 
            className="card mb-3" 
            style={{ 
              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
              border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6"
            }}
          >
            <div className="card-body">
              <h6 className="mb-3" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>Add New Price</h6>
              <div className="row g-3">
                <div className="col-md-3">
                  <label>Currency</label>
                  <CurrencySelector
                    value={newPrice.currency?.value}
                    onChange={(option) =>
                      setNewPrice({ ...newPrice, currency: option })
                    }
                    placeholder="Select Currency"
                    isClearable={true}
                  />
                </div>
                <div className="col-md-2">
                  <label>Price</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="99.00"
                    value={newPrice.price}
                    onChange={(e) => handlePriceInputChange(e, "price")}
                    step="0.01"
                    min="0"
                    style={{
                      borderRadius: "10px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#495057",
                      height: "44px",
                    }}
                  />
                  <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                    Total payment for agreement period
                  </small>
                </div>
                <div className="col-md-2">
                  <label>Actual Price</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="149.00"
                    value={newPrice.actualPrice}
                    onChange={(e) => handlePriceInputChange(e, "actualPrice")}
                    step="0.01"
                    min="0"
                    style={{
                      borderRadius: "10px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#495057",
                      height: "44px",
                    }}
                  />
                  <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Before discount</small>
                </div>
                <div className="col-md-2">
                  <label>Agreement (months)</label>
                  <select
                    className="form-control"
                    value={newPrice.useCustomMonths ? "custom" : String(newPrice.agreementMonths)}
                    onChange={(e) => {
                      if (e.target.value === "custom") {
                        setNewPrice({ ...newPrice, useCustomMonths: true });
                      } else {
                        setNewPrice({
                          ...newPrice,
                          useCustomMonths: false,
                          agreementMonths: parseInt(e.target.value, 10),
                        });
                      }
                    }}
                    style={{
                      borderRadius: "10px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#495057",
                      height: "44px",
                    }}
                  >
                    {AGREEMENT_MONTH_PRESETS.map((m) => (
                      <option key={m} value={m}>
                        {formatAgreementMonths(m)}
                      </option>
                    ))}
                    <option value="custom">Custom...</option>
                  </select>
                  {newPrice.useCustomMonths && (
                    <input
                      type="number"
                      className="form-control mt-1"
                      placeholder="e.g. 3"
                      min="1"
                      max="120"
                      value={newPrice.customMonths}
                      onChange={(e) =>
                        setNewPrice({ ...newPrice, customMonths: e.target.value })
                      }
                      style={{
                        borderRadius: "10px",
                        border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                        background: isDarkMode ? "#2d3436" : "#F9F9F9",
                        color: isDarkMode ? "#ffffff" : "#495057",
                        height: "40px",
                      }}
                    />
                  )}
                  <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
                    Length of subscription agreement
                  </small>
                </div>
                <div className="col-md-3">
                  <label>Country (Optional)</label>
                  <CountrySelector
                    value={newPrice.country}
                    onChange={(countryCode) =>
                      setNewPrice({ ...newPrice, country: countryCode })
                    }
                    placeholder="Select Country"
                    isClearable={true}
                  />
                  <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Select country for pricing</small>
                </div>
                <div className="col-md-2">
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={handleAddPrice}
                  >
                    <i className="fa fa-plus me-1"></i> Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Price List Table */}
          {prices.length > 0 && (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead
                  style={{
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Currency</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Price</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actual Price</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Agreement</th>
                    <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Country</th>
                    <th className="text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {prices.map((price) => {
                    const discount =
                      price.actualPrice > 0
                        ? (
                            ((price.actualPrice - price.price) /
                              price.actualPrice) *
                            100
                          ).toFixed(0)
                        : 0;

                    return (
                      <tr key={price.id}>
                        <td>
                          <strong>{price.currency.label}</strong>
                        </td>
                        <td>{price.currency.value} {parseFloat(price.price).toFixed(2)}</td>
                        <td>
                          {price.actualPrice > 0
                            ? `${price.currency.value} ${parseFloat(price.actualPrice).toFixed(2)}`
                            : "-"}
                        </td>
                        <td>
                          <span className="badge bg-info">
                            {formatAgreementMonths(price.agreementMonths)}
                          </span>
                        </td>
                        <td>{price.country || "-"}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-danger"
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: '1px solid #dc3545',
                              color: '#dc3545',
                              transition: 'all 0.2s ease',
                              padding: 0
                            }}
                            onClick={() => handleRemovePrice(price.id)}
                            title="Delete Price"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#dc3545';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = '#dc3545';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {prices.length === 0 && (
            <div className="alert alert-warning">
              <i className="fa fa-exclamation-triangle me-2"></i>
              No pricing added yet. Add at least one price for your package.
            </div>
          )}
        </PanelBody>
      </Panel>

    </div>
  );
}

export default AddPackage;

